import { useViewerContext } from "./useViewerContext";

/**
 * Hook to get the current user's permissions
 * @returns Array of permission strings (snake_case format matching backend)
 */
export function usePermissions(): string[] {
    const { viewer } = useViewerContext();
    return viewer?.permissions ?? [];
}

/**
 * Hook to check if the current user has a specific permission
 * @param permission - The permission to check (snake_case format)
 * @returns Boolean indicating if the user has the permission
 */
export function useHasPermission(permission: string): boolean {
    const permissions = usePermissions();
    return permissions.includes(permission);
}

/**
 * Hook to check if the current user has any of the specified permissions
 * @param requiredPermissions - Array of permissions to check
 * @returns Boolean indicating if the user has at least one of the permissions
 */
export function useHasAnyPermission(requiredPermissions: string[]): boolean {
    const permissions = usePermissions();
    return requiredPermissions.some(permission => permissions.includes(permission));
}

/**
 * Hook to check if the current user has all of the specified permissions
 * @param requiredPermissions - Array of permissions to check
 * @returns Boolean indicating if the user has all of the permissions
 */
export function useHasAllPermissions(requiredPermissions: string[]): boolean {
    const permissions = usePermissions();
    return requiredPermissions.every(permission => permissions.includes(permission));
}

// Permission constants for type-safe usage
export const Permissions = {
    ACCESS_AI_FEATURES: "access_ai_features",
    CONTENT_MANAGEMENT: "content_management",
    TOURNAMENT_OPERATIONS: "tournament_operations",
    PLAYER_MANAGEMENT: "player_management",
    GAME_COMMENTARY: "game_commentary",
    USER_ADMINISTRATION: "user_administration",
    INFRASTRUCTURE_CONTROL: "infrastructure_control",
    BROADCAST_NOTIFICATIONS: "broadcast_notifications",
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];
